import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { getEventImageUrl, getPlaceholderImage } from '../utils/imageUtils';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideUp} 0.4s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #FF9500;
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    max-height: 95vh;
    margin: 10px;
    border-radius: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 18px;
  z-index: 2;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
    transform: scale(1.1);
  }
`;

const EventImage = styled.div`
  width: 100%;
  height: 250px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 16px 16px 0 0;
  background-image: ${props => props.image ? `url(${props.image})` : 'linear-gradient(135deg, #FF9500, #FFB84D)'};
  
  &::after {
    content: ${props => !props.image ? '"🎉"' : '""'};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 4rem;
    opacity: 0.7;
  }
`;

const EventBadge = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: #FF9500;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 149, 0, 0.3);
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const EventTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  color: #666;
  
  i {
    width: 20px;
    color: #FF9500;
    font-size: 1.1rem;
  }
  
  strong {
    color: #333;
  }
`;

const EventDescription = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 25px;
  white-space: pre-wrap;
`;

const CategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
`;

const CategoryTag = styled.span`
  background: #f0f9ff;
  color: #0369a1;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #e0f2fe;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  ${props => props.primary ? `
    background: #FF9500;
    color: white;
    
    &:hover {
      background: #e8860a;
      transform: translateY(-1px);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #e9ecef;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const EventModal = ({ event, isOpen, onClose }) => {
  // ✅ HOOKS PRIMA DEL RETURN CONDIZIONALE
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // ✅ RETURN CONDIZIONALE DOPO GLI HOOKS
  if (!isOpen || !event) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Data non disponibile';
    
    try {
      const date = new Date(dateString);
      const day = date.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      const time = date.toLocaleDateString('it-IT', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${day} alle ore ${time}`;
    } catch (error) {
      return 'Data non valida';
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Gratis';
    if (price === 0) return 'Gratis';
    return `€${price}`;
  };

  const getImageUrl = () => {
    const imageUrl = getEventImageUrl(event, 'large');
    return imageUrl || getPlaceholderImage(event);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewOnMap = () => {
    onClose();
    window.location.href = `/app?event=${event.id}`;
  };

  const handleOpenExternal = () => {
    if (event.url) {
      window.open(event.url, '_blank');
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <EventImage image={getImageUrl()}>
          <EventBadge>{formatPrice(event.ticketPrice)}</EventBadge>
          <CloseButton onClick={onClose}>
            <i className="fas fa-times"></i>
          </CloseButton>
        </EventImage>
        
        <ModalBody>
          <EventTitle>{event.title}</EventTitle>
          
          <EventDetails>
            <EventDetail>
              <i className="fas fa-calendar-alt"></i>
              <span><strong>Data:</strong> {formatDate(event.startDate)}</span>
            </EventDetail>
            
            <EventDetail>
              <i className="fas fa-map-marker-alt"></i>
              <span><strong>Luogo:</strong> {event.location || 'Luogo da definire'}</span>
            </EventDetail>
            
            {event.ticketPrice !== null && event.ticketPrice !== undefined && (
              <EventDetail>
                <i className="fas fa-ticket-alt"></i>
                <span><strong>Prezzo:</strong> {formatPrice(event.ticketPrice)}</span>
              </EventDetail>
            )}
            
            {event.phone && (
              <EventDetail>
                <i className="fas fa-phone"></i>
                <span><strong>Telefono:</strong> {event.phone}</span>
              </EventDetail>
            )}
            
            {event.email && (
              <EventDetail>
                <i className="fas fa-envelope"></i>
                <span><strong>Email:</strong> {event.email}</span>
              </EventDetail>
            )}
          </EventDetails>
          
          {event.categories && event.categories.length > 0 && (
            <CategoryTags>
              {event.categories.map((category, index) => (
                <CategoryTag key={index}>{category.name}</CategoryTag>
              ))}
            </CategoryTags>
          )}
          
          {event.description && (
            <EventDescription>
              {event.description}
            </EventDescription>
          )}
          
          <ActionButtons>
            <ActionButton primary onClick={handleViewOnMap}>
              <i className="fas fa-map"></i>
              Vedi sulla Mappa
            </ActionButton>
            
            {event.url && (
              <ActionButton onClick={handleOpenExternal}>
                <i className="fas fa-external-link-alt"></i>
                Sito Web
              </ActionButton>
            )}
          </ActionButtons>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EventModal;
